import React, { useState, useEffect, useCallback } from 'react';
import {
  UtensilsCrossed, Plus, Download, Upload, Trash2, Check, X, Edit3,
  Calendar, Coffee, Sun as SunIcon, Cookie, Moon, ChevronDown, ChevronUp,
  FileSpreadsheet, AlertCircle, CheckCircle2, Loader2, Star, History
} from 'lucide-react';
import apiClient from '@/utils/apiClient';
import downloadFile from '@/utils/fileDownloader';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MEALS = [
  { key: 'breakfast', label: 'Breakfast', icon: Coffee, color: '#F59E0B', bg: '#FFFBEB', border: '#FDE68A' },
  { key: 'lunch', label: 'Lunch', icon: SunIcon, color: '#10B981', bg: '#ECFDF5', border: '#A7F3D0' },
  { key: 'snacks', label: 'Snacks', icon: Cookie, color: '#8B5CF6', bg: '#F5F3FF', border: '#DDD6FE' },
  { key: 'dinner', label: 'Dinner', icon: Moon, color: '#3B82F6', bg: '#EFF6FF', border: '#BFDBFE' }
];

const DAY_SHORT = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

const emptyMeals = () => {
  const meals = {};
  DAYS.forEach(day => {
    meals[day] = {};
    MEALS.forEach(m => { meals[day][m.key] = []; });
  });
  return meals;
};

const NutritionManagement = () => {
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    week_label: '',
    start_date: '',
    end_date: '',
    plan_type: 'standard',
    meals: emptyMeals()
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedDay, setExpandedDay] = useState('Monday');
  const [showHistory, setShowHistory] = useState(false);
  const [itemInputs, setItemInputs] = useState({});

  const getDisplayDays = useCallback(() => {
    if (formData.plan_type === 'standard') {
      return DAYS.map((day, index) => {
        let dateStr = '';
        if (formData.start_date) {
          const d = new Date(formData.start_date + 'T00:00:00');
          d.setDate(d.getDate() + index);
          dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
        return {
          day,
          dateLabel: dateStr ? ` (${dateStr})` : ''
        };
      });
    } else {
      if (!formData.start_date || !formData.end_date) {
        return DAYS.map(day => ({
          day,
          dateLabel: ''
        }));
      }

      const start = new Date(formData.start_date + 'T00:00:00');
      const end = new Date(formData.end_date + 'T00:00:00');
      
      const list = [];
      const current = new Date(start);
      let limit = 0;
      while (current <= end && limit < 14) {
        const dayName = current.toLocaleDateString('en-US', { weekday: 'long' });
        const dateStr = current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        list.push({
          day: dayName,
          dateLabel: ` (${dateStr})`
        });
        current.setDate(current.getDate() + 1);
        limit++;
      }
      
      if (list.length === 0) {
        return DAYS.map(day => ({
          day,
          dateLabel: ''
        }));
      }
      return list;
    }
  }, [formData.plan_type, formData.start_date, formData.end_date]);

  const displayDays = getDisplayDays();

  useEffect(() => {
    if (showForm) {
      const dayNames = displayDays.map(d => d.day);
      if (dayNames.length > 0 && !dayNames.includes(expandedDay)) {
        setExpandedDay(dayNames[0]);
      }
    }
  }, [showForm, displayDays, expandedDay]);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const [allRes, activeRes] = await Promise.all([
        apiClient.get('/v1/admin/nutrition/plans'),
        apiClient.get('/v1/admin/nutrition/plans/active')
      ]);
      setPlans(allRes.data.plans || []);
      setActivePlan(activeRes.data.plan || null);
    } catch (e) {
      console.error('Failed to fetch plans:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleCreateNew = () => {
    setEditingPlan(null);
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    setFormData({
      week_label: `Week of ${monday.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`,
      start_date: monday.toISOString().split('T')[0],
      end_date: sunday.toISOString().split('T')[0],
      plan_type: 'standard',
      meals: emptyMeals()
    });
    setItemInputs({});
    setExpandedDay('Monday');
    setShowForm(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    
    // Create a fresh template
    const baseMeals = emptyMeals();
    
    // Parse existing meals (handle string or object)
    let existingMeals = plan.meals || {};
    if (typeof existingMeals === 'string') {
      try {
        existingMeals = JSON.parse(existingMeals);
      } catch (e) {
        existingMeals = {};
      }
    }

    // Merge existing data into the template to ensure all keys exist
    DAYS.forEach(day => {
      if (existingMeals[day]) {
        MEALS.forEach(meal => {
          if (existingMeals[day][meal.key]) {
            baseMeals[day][meal.key] = Array.isArray(existingMeals[day][meal.key]) 
              ? [...existingMeals[day][meal.key]]
              : [];
          }
        });
      }
    });

    setFormData({
      week_label: plan.week_label || '',
      start_date: plan.start_date || '',
      end_date: plan.end_date || '',
      plan_type: plan.plan_type || 'standard',
      meals: baseMeals
    });
    
    setItemInputs({});
    setExpandedDay('Monday');
    setShowForm(true);
  };

  const handleAddItem = (day, mealKey) => {
    const inputKey = `${day}_${mealKey}`;
    const value = (itemInputs[inputKey] || '').trim();
    if (!value) return;

    setFormData(prev => ({
      ...prev,
      meals: {
        ...prev.meals,
        [day]: {
          ...(prev.meals[day] || {}),
          [mealKey]: [...(prev.meals[day]?.[mealKey] || []), value]
        }
      }
    }));
    
    setItemInputs(prev => ({ ...prev, [inputKey]: '' }));
  };

  const handleRemoveItem = (day, mealKey, index) => {
    setFormData(prev => ({
      ...prev,
      meals: {
        ...prev.meals,
        [day]: {
          ...prev.meals[day],
          [mealKey]: (prev.meals[day][mealKey] || []).filter((_, i) => i !== index)
        }
      }
    }));
  };

  const handleSave = async () => {
    if (!formData.week_label || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in Week Label, Start Date, and End Date');
      return;
    }

    // Check if there are any typed items in the input fields that haven't been added (by clicking the Plus button)
    const hasUnsavedTypedItems = Object.values(itemInputs).some(value => value && value.trim() !== '');
    if (hasUnsavedTypedItems) {
      toast.error('⚠️ There are unsaved items in the input fields. Kindly click the plus icon to add them, or clear the input fields before saving.');
      return;
    }

    // Ensure we have a clean copy of the meals to send
    const payload = {
      week_label: formData.week_label,
      start_date: formData.start_date,
      end_date: formData.end_date,
      plan_type: formData.plan_type || 'standard',
      meals: formData.meals
    };

    setSaving(true);
    try {
      if (editingPlan) {
        await apiClient.put(`/v1/admin/nutrition/plans/${editingPlan.id}`, payload);
        toast.success('✅ Nutrition plan updated successfully!');
      } else {
        await apiClient.post('/v1/admin/nutrition/plans', payload);
        toast.success('✅ Nutrition plan created successfully!');
      }
      setShowForm(false);
      fetchPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save nutrition plan');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (planId) => {
    try {
      await apiClient.post(`/v1/admin/nutrition/plans/${planId}/activate`);
      toast.success('✅ Plan activated successfully!');
      fetchPlans();
    } catch (e) {
      toast.error('Failed to activate plan');
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    try {
      await apiClient.delete(`/v1/admin/nutrition/plans/${planId}`);
      toast.success('🗑️ Plan deleted');
      fetchPlans();
    } catch (e) {
      toast.error('Failed to delete plan');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get('/v1/admin/nutrition/template', { responseType: 'blob' });
      const mimeType = response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      await downloadFile(response.data, 'nutrition_plan_template.xlsx', mimeType);
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post('/v1/admin/nutrition/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`✅ ${response.data.message}`);
      fetchPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const getTotalItems = (meals) => {
    if (!meals) return 0;
    return Object.values(meals).reduce((total, dayMeals) =>
      total + Object.values(dayMeals).reduce((dayTotal, items) => dayTotal + (items?.length || 0), 0)
    , 0);
  };

  // ============ Render ============

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" style={{ color: '#4A90D9' }} />
          <p className="text-gray-500">Loading nutrition plans...</p>
        </div>
      </div>
    );
  }

  // ---- Form View (Create/Edit) ----
  if (showForm) {
    return (
      <div className="max-w-5xl mx-auto">
        {/* Form Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold" style={{ color: '#2C5F7F' }}>
              {editingPlan ? '✏️ Edit Nutrition Plan' : '🍽️ Create Nutrition Plan'}
            </h2>
            <p className="text-gray-500 mt-1">Set day-wise meals for breakfast, lunch, snacks & dinner</p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowForm(false)}
            className="gap-2"
          >
            <X className="w-4 h-4" /> Cancel
          </Button>
        </div>

        {/* Metadata Fields */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          {/* Plan Type Selector */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Plan Type</label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, plan_type: 'standard' }))}
                className={`flex-1 py-3 px-5 rounded-xl font-bold border transition-all text-center flex items-center justify-center gap-2 ${
                  formData.plan_type === 'standard'
                    ? 'bg-blue-50 text-blue-600 border-blue-200 shadow-sm'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Calendar className="w-5 h-5" />
                Standard Weekly Plan
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, plan_type: 'occasion' }))}
                className={`flex-1 py-3 px-5 rounded-xl font-bold border transition-all text-center flex items-center justify-center gap-2 ${
                  formData.plan_type === 'occasion'
                    ? 'bg-purple-50 text-purple-600 border-purple-200 shadow-sm'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Star className="w-5 h-5 animate-pulse" />
                Special Occasion Plan
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 font-medium">
              {formData.plan_type === 'standard'
                ? '💡 A standard weekly nutrition plan runs indefinitely as the base meal schedule.'
                : '🎉 An occasion-related meal plan overrides the standard menu during the specified date range.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {formData.plan_type === 'occasion' ? 'Occasion Name *' : 'Week Label *'}
              </label>
              <input
                type="text"
                value={formData.week_label}
                onChange={(e) => setFormData(prev => ({ ...prev, week_label: e.target.value }))}
                className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none transition-all"
                placeholder={formData.plan_type === 'occasion' ? 'e.g., Diwali Festival Special Menu' : 'Week of May 12, 2026'}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Start Date *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">End Date *</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none transition-all"
              />
            </div>
          </div>
        </div>

        {/* Day-wise Meal Editor */}
        <div className="space-y-3">
          {displayDays.map(({ day, dateLabel }) => (
            <div key={`${day}_${dateLabel}`} className="bg-white rounded-xl shadow-sm border overflow-hidden">
              {/* Day Header */}
              <button
                type="button"
                onClick={() => setExpandedDay(expandedDay === day ? null : day)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                    style={{ background: 'linear-gradient(135deg, #4A90D9, #357ABD)' }}>
                    {DAY_SHORT[day]}
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-800 flex items-center gap-1.5">
                      {day}
                      {dateLabel && <span className="text-xs font-normal text-gray-500">{dateLabel}</span>}
                    </h3>
                    <p className="text-xs text-gray-400">
                      {Object.values(formData.meals[day] || {}).reduce((t, items) => t + (items?.length || 0), 0)} items added
                    </p>
                  </div>
                </div>
                {expandedDay === day ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </button>

              {/* Meal Categories (expanded) */}
              {expandedDay === day && (
                <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {MEALS.map(meal => {
                    const items = formData.meals[day]?.[meal.key] || [];
                    const inputKey = `${day}_${meal.key}`;
                    const MealIcon = meal.icon;
                    return (
                      <div key={meal.key} className="rounded-xl p-4 border" style={{ background: meal.bg, borderColor: meal.border }}>
                        <div className="flex items-center gap-2 mb-3">
                          <MealIcon className="w-5 h-5" style={{ color: meal.color }} />
                          <span className="font-semibold text-sm" style={{ color: meal.color }}>{meal.label}</span>
                          <span className="ml-auto text-xs text-gray-400">{items.length} items</span>
                        </div>

                        {/* Items list */}
                        <div className="flex flex-wrap gap-2 mb-3 min-h-[32px]">
                          {items.map((item, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-white rounded-full text-sm shadow-sm border" style={{ borderColor: meal.border }}>
                              {item}
                              <button onClick={() => handleRemoveItem(day, meal.key, idx)} className="ml-1 hover:text-red-500 transition-colors">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          ))}
                          {items.length === 0 && (
                            <span className="text-xs text-gray-400 italic">No items added yet</span>
                          )}
                        </div>

                        {/* Add item input */}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={itemInputs[inputKey] || ''}
                            onChange={(e) => setItemInputs(prev => ({ ...prev, [inputKey]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddItem(day, meal.key); } }}
                            className="flex-1 px-3 py-2 bg-white border rounded-lg text-sm focus:ring-2 focus:ring-blue-200 outline-none"
                            placeholder={`Add ${meal.label.toLowerCase()} item...`}
                            style={{ borderColor: meal.border }}
                          />
                          <button
                            onClick={() => handleAddItem(day, meal.key)}
                            className="px-3 py-2 rounded-lg text-white transition-all hover:opacity-90 active:scale-95"
                            style={{ backgroundColor: meal.color }}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="gap-2 px-8 text-white"
            style={{ backgroundColor: '#4A90D9' }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {saving ? 'Saving...' : (editingPlan ? 'Update Plan' : 'Create Plan')}
          </Button>
        </div>
      </div>
    );
  }

  // ---- Main View (List + Active Plan) ----
  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: '#2C5F7F' }}>
            <UtensilsCrossed className="w-8 h-8" style={{ color: '#4A90D9' }} />
            Nutrition Management
          </h1>
          <p className="text-gray-500 mt-1">Manage weekly meal plans for your hostel</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleDownloadTemplate} className="gap-2">
            <Download className="w-4 h-4" /> Download Template
          </Button>
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg cursor-pointer hover:bg-emerald-700 transition-colors text-sm font-medium">
            <Upload className="w-4 h-4" />
            {uploading ? 'Importing...' : 'Import Sheet'}
            <input type="file" accept=".xlsx,.xls" onChange={handleImport} disabled={uploading} className="hidden" />
          </label>
          <Button onClick={handleCreateNew} className="gap-2 text-white" style={{ backgroundColor: '#4A90D9' }}>
            <Plus className="w-4 h-4" /> Create Plan
          </Button>
        </div>
      </div>

      {/* Import Info Banner */}
      <div className="mb-6 p-4 rounded-xl border border-blue-200 bg-blue-50/60 flex items-start gap-3">
        <FileSpreadsheet className="w-5 h-5 mt-0.5 text-blue-500 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-800">Quick Import via Excel Sheet</p>
          <p className="text-xs text-blue-600 mt-0.5">
            Download the template → Fill in your weekly meal plan → Upload it back. The system will automatically parse and create your nutrition plan.
          </p>
        </div>
      </div>

      {/* Active Plan Display */}
      {activePlan ? (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Star className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-bold text-gray-800">Active Plan</h2>
            <span className="px-3 py-0.5 text-xs font-semibold bg-green-100 text-green-700 rounded-full">ACTIVE</span>
          </div>

          <div className="bg-white rounded-2xl shadow-md border overflow-hidden">
            {/* Plan header */}
            <div className="p-6 border-b" style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{activePlan.week_label}</h3>
                  <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {activePlan.start_date} — {activePlan.end_date}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(activePlan)} className="gap-1">
                    <Edit3 className="w-3.5 h-3.5" /> Edit
                  </Button>
                </div>
              </div>
            </div>

            {/* Weekly meal grid */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Day</th>
                    {MEALS.map(meal => {
                      const MealIcon = meal.icon;
                      return (
                        <th key={meal.key} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: meal.color }}>
                          <div className="flex items-center gap-1.5">
                            <MealIcon className="w-4 h-4" />
                            {meal.label}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {DAYS.map((day, dayIdx) => (
                    <tr key={day} className={`border-b last:border-0 ${dayIdx % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'}`}>
                      <td className="px-4 py-4">
                        <span className="font-semibold text-gray-700">{DAY_SHORT[day]}</span>
                      </td>
                      {MEALS.map(meal => {
                        const items = activePlan.meals?.[day]?.[meal.key] || [];
                        return (
                          <td key={meal.key} className="px-4 py-4">
                            <div className="flex flex-wrap gap-1.5">
                              {items.length > 0 ? items.map((item, idx) => (
                                <span key={idx} className="px-2.5 py-1 rounded-full text-xs font-medium border" style={{ background: meal.bg, color: meal.color, borderColor: meal.border }}>
                                  {item}
                                </span>
                              )) : (
                                <span className="text-gray-300 text-xs italic">—</span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-8 text-center py-16 bg-white rounded-2xl shadow-sm border">
          <UtensilsCrossed className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No Active Nutrition Plan</h3>
          <p className="text-gray-400 mb-6">Create a new meal plan or import one from an Excel sheet</p>
          <div className="flex justify-center gap-3">
            <Button onClick={handleCreateNew} className="gap-2 text-white" style={{ backgroundColor: '#4A90D9' }}>
              <Plus className="w-4 h-4" /> Create Plan
            </Button>
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg cursor-pointer hover:bg-emerald-700 transition-colors text-sm font-medium">
              <Upload className="w-4 h-4" /> Import Sheet
              <input type="file" accept=".xlsx,.xls" onChange={handleImport} disabled={uploading} className="hidden" />
            </label>
          </div>
        </div>
      )}

      {/* Active Occasion Overlays */}
      {activePlan?.occasion_plans && activePlan.occasion_plans.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Star className="w-5 h-5 text-purple-500 animate-pulse" />
            <h2 className="text-lg font-bold text-gray-800">Active Occasion Overlays</h2>
            <span className="px-3 py-0.5 text-xs font-semibold bg-purple-100 text-purple-700 rounded-full border border-purple-200">OVERRIDE ACTIVE</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activePlan.occasion_plans.map(occPlan => (
              <div key={occPlan.id} className="bg-white rounded-xl shadow-sm border p-5 flex items-center justify-between border-purple-200 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FAF5FF 0%, #FFFFFF 100%)' }}>
                <div className="absolute top-0 right-0 w-24 h-24 bg-purple-100/30 rounded-full -mr-8 -mt-8 flex items-center justify-center">
                  <Star className="w-8 h-8 text-purple-200" />
                </div>
                <div className="z-10">
                  <h4 className="font-bold text-purple-900 text-lg flex items-center gap-2">
                    ✨ {occPlan.week_label}
                  </h4>
                  <p className="text-sm text-purple-700 flex items-center gap-2 mt-1">
                    <Calendar className="w-4 h-4 text-purple-400" />
                    {occPlan.start_date} — {occPlan.end_date}
                  </p>
                  <p className="text-xs text-purple-500 mt-2 font-medium">
                    This menu overrides the standard menu during these dates.
                  </p>
                </div>
                <div className="flex flex-col gap-2 z-10">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(occPlan)} className="gap-1 border-purple-200 hover:bg-purple-50 text-purple-700 bg-white">
                    <Edit3 className="w-3.5 h-3.5" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(occPlan.id)} className="gap-1 text-red-500 border-red-200 hover:bg-red-50 bg-white">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Plan History */}
      {plans.length > 1 && (
        <div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors mb-4"
          >
            <History className="w-5 h-5" />
            <span className="font-semibold">Plan History ({plans.length - (activePlan ? 1 : 0)} previous)</span>
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showHistory && (
            <div className="space-y-3">
              {plans.filter(p => p.id !== activePlan?.id).map(plan => (
                <div key={plan.id} className="bg-white rounded-xl shadow-sm border p-5 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-800">{plan.week_label}</h4>
                      {plan.plan_type === 'occasion' ? (
                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700 rounded-full border border-purple-200">
                          <Star className="w-2.5 h-2.5 fill-purple-400 text-purple-600" /> Occasion
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full border border-blue-200">
                          <Calendar className="w-2.5 h-2.5 text-blue-600" /> Standard
                        </span>
                      )}
                      {plan.is_active && (
                        <span className="px-2 py-0.5 text-[10px] font-bold bg-green-100 text-green-700 rounded-full border border-green-200">
                          🟢 ACTIVE
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {plan.start_date} — {plan.end_date}
                      <span className="text-gray-300">•</span>
                      <span>{getTotalItems(plan.meals)} items</span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!plan.is_active && (
                      <Button variant="outline" size="sm" onClick={() => handleActivate(plan.id)} className="gap-1 text-green-600 border-green-200 hover:bg-green-50">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Activate
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleEdit(plan)} className="gap-1">
                      <Edit3 className="w-3.5 h-3.5" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(plan.id)} className="gap-1 text-red-500 border-red-200 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NutritionManagement;
